import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import { InvoiceScheduleType } from './InvoiceTypes';
import '../../style/scss/card.scss';

// Set the interval options of the repetition dropdown
export const INVOICE_INTERVAL_OPTIONS = [
    { value: 'weekly', name: 'contract.interval.weekly' },
    { value: 'monthly', name: 'contract.interval.monthly' },
    { value: 'quarterly', name: 'contract.interval.quarterly' },
    { value: 'half_yearly', name: 'contract.interval.half_yearly' },
    { value: 'yearly', name: 'contract.interval.yearly' },
];

// Renders the running or stopped badge of a schedule
export const renderScheduleStatusBadge = (isActive: boolean, t: (key: string) => string) => (
    <div className='badge-container'>
        <div className={`status-badge ${isActive ? 'contract-active' : 'contract-cancelled'}`}>
            {t(isActive ? 'invoice.schedule.status_active' : 'invoice.schedule.status_stopped')}
        </div>
    </div>
);

// Formats a date value to the user locale
export const formatScheduleDate = (value: string | null | undefined, userLocale: string) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString(userLocale, { year: 'numeric', month: '2-digit', day: '2-digit' });
};

// Builds the sentence describing the rhythm and the next invoice of a schedule
export const buildScheduleSentence = (schedule: InvoiceScheduleType, t: (key: string, options?: any) => string, userLocale: string) => {
    const repetition = t(`invoice.schedule.every.${schedule.interval_type}`);
    if (!schedule.is_active) {
        return t('invoice.schedule.sentence_stopped', { repetition });
    }
    if (!schedule.next_invoice_date) {
        return t('invoice.schedule.sentence_ended', { repetition });
    }
    return t('invoice.schedule.sentence_next', { repetition, date: formatScheduleDate(schedule.next_invoice_date, userLocale) });
};

// Shows the rhythm of a periodic invoice as a compact block
const InvoiceScheduleCard: React.FC<{
    data?: any;
    onClick?: () => void;
}> = ({
    data,
    onClick,
}) => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();

    // Get the schedule of the template
    const schedule: InvoiceScheduleType | null = data?.schedule ?? null;

    // Without a schedule the block has nothing to show
    if (!schedule) return null;

    return (
        <div className={`invoice-schedule-block ${onClick ? 'clickable' : ''}`} onClick={onClick}>
            <div className='invoice-schedule-block-title'>
                {t('invoice.schedule.card_label')}
                {!schedule.is_active && (
                    <span className='invoice-schedule-block-status'>{t('invoice.schedule.status_stopped').toLowerCase()}</span>
                )}
            </div>
            <div className='invoice-schedule-block-sentence'>
                {buildScheduleSentence(schedule, t, userLocale)}
            </div>
        </div>
    );
};

export default InvoiceScheduleCard;
